import * as yup from "yup";

const hasNameDescriptionAndModelId = yup
  .object({
    name: yup.string().required(),
    description: yup.string().required(),
    modelId: yup.string(),
    model_id: yup.string(),
  })
  .defined();

type ColumnDescriptionDict = Record<string, Record<string, string | null>>;

/**
 * helper function for clarity: transform filterable_audience_columns, source_column_descriptions, or query_dbt_model columns into
 * a simple Record<string, string> dict where key is the lowercase column name, value is the description.
 * Omit missing descriptions from the result dict.
 */
function toColumnDescriptionDict(
  input: unknown,
  defaultModelId: string,
): Record<string, Record<string, string | null>> {
  if (!Array.isArray(input)) {
    return {};
  }

  return input
    .filter((val) => hasNameDescriptionAndModelId.isValidSync(val))
    .reduce((dict, { modelId, model_id, name, description }) => {
      const id = modelId ?? model_id ?? defaultModelId;
      if (!dict[id]) {
        dict[id] = {};
      }
      dict[id][name.toLowerCase()] = description;
      return dict;
    }, {} as ColumnDescriptionDict);
}

interface Column {
  name: string;
  modelId?: string;
  model_id?: string;
  description?: string | null;
}

type ColumnInput = Column[] | null;

export class ColumnDescriptionHelper {
  columnDescriptions: ColumnDescriptionDict;
  filterableColumnDescriptions: ColumnDescriptionDict;
  dbtColumnDescriptions: ColumnDescriptionDict;
  sourceColumnDescriptions: ColumnDescriptionDict;
  defaultModelId: string;

  /** Note that `filterableAudienceColumns` should merge dbt and source descriptions already on the backend */
  constructor({
    columns,
    filterableAudienceColumns,
    dbtColumns,
    sourceColumns,
    // Random default id
    defaultModelId = "__ht_internal_frontend_9321775113",
  }: {
    columns?: ColumnInput;
    filterableAudienceColumns?: ColumnInput;
    dbtColumns?: ColumnInput;
    sourceColumns?: ColumnInput;
    /** Columns without model ids are assumed to be part of this model */
    defaultModelId?: string;
  }) {
    this.columnDescriptions = toColumnDescriptionDict(columns, defaultModelId);
    this.filterableColumnDescriptions = toColumnDescriptionDict(
      filterableAudienceColumns,
      defaultModelId,
    );
    this.dbtColumnDescriptions = toColumnDescriptionDict(
      dbtColumns,
      defaultModelId,
    );
    this.sourceColumnDescriptions = toColumnDescriptionDict(
      sourceColumns,
      defaultModelId,
    );
    this.defaultModelId = defaultModelId;
  }

  /**
   * Select and format a column description for display.
   * Priority to user defined description -> dbt -> source schema (eg. table
   * comments)
   *
   * If `modelId` is not specified, uses the parent model id */
  getColumnDescription = (name: string, modelId = this.defaultModelId) => {
    const lowercaseName = name.toLowerCase();
    return (
      this.columnDescriptions[modelId]?.[lowercaseName] ||
      this.filterableColumnDescriptions[modelId]?.[lowercaseName] ||
      this.dbtColumnDescriptions[modelId]?.[lowercaseName] ||
      this.sourceColumnDescriptions[modelId]?.[lowercaseName] ||
      null
    );
  };
}
